import axios from "../axios";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import EditStudentForm from "../components/EditStudentForm";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { queryClient } from "../index";
import { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import LeavingCertificate from "../components/LeavingCertificate";

const LEAVING_REASONS = [
  { value: "MOVING_ABROAD", label: "Moving Abroad" },
  { value: "TRANSFER_TO_ANOTHER_SCHOOL", label: "Transfer to Another School" },
  { value: "FINANCIAL", label: "Financial" },
  { value: "GRADUATED", label: "Graduated" },
  { value: "PERSONAL", label: "Personal" },
  { value: "OTHER", label: "Other" },
];

const OPTIONAL_FIELDS = [
  { name: "academicAchievements", label: "Academic Achievements" },
  { name: "coCurricularActivities", label: "Co-curricular Activities" },
  { name: "specialPostHeld", label: "Special Post Held" },
  { name: "scholarshipsAwards", label: "Scholarships / Awards Received" },
];

const MarkAsLeftDialog = ({ open, onClose, studentId }) => {
  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {
      leavingDate: dayjs(),
      reasonForLeaving: "",
      reasonForLeavingOther: "",
      character: "",
      academicAchievements: "",
      coCurricularActivities: "",
      specialPostHeld: "",
      scholarshipsAwards: "",
    },
  });

  const selectedReason = watch("reasonForLeaving");

  const [enabledFields, setEnabledFields] = useState({
    academicAchievements: false,
    coCurricularActivities: false,
    specialPostHeld: false,
    scholarshipsAwards: false,
  });

  const handleToggle = (fieldName) => {
    setEnabledFields((prev) => {
      const newState = { ...prev, [fieldName]: !prev[fieldName] };
      if (!newState[fieldName]) {
        setValue(fieldName, "");
      }
      return newState;
    });
  };

  const leaveMutation = useMutation((data) =>
    axios.post(`/students/${studentId}/leave`, data)
  );

  const onSubmit = (data) => {
    leaveMutation.mutate(
      {
        ...data,
        leavingDate: data.leavingDate.format("DD-MM-YYYY"),
      },
      {
        onSuccess: () => {
          toast.success("Student marked as left and certificate created");
          queryClient.invalidateQueries(["student", studentId]);
          onClose();
        },
        onError: (err) => {
          toast.error(err.response?.data?.message || "Something went wrong");
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Mark Student as Left</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="leave-form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, mt: 1 }}
        >
          <Controller
            render={({ field }) => (
              <DatePicker
                {...field}
                label="Date of Leaving"
                inputFormat="DD-MM-YYYY"
                renderInput={(params) => <TextField {...params} required />}
              />
            )}
            control={control}
            name="leavingDate"
          />
          <Controller
            render={({ field }) => (
              <FormControl fullWidth required>
                <InputLabel id="reason-label">Reason for Leaving</InputLabel>
                <Select {...field} labelId="reason-label" label="Reason for Leaving">
                  {LEAVING_REASONS.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            control={control}
            name="reasonForLeaving"
          />
          {selectedReason === "OTHER" && (
            <Controller
              render={({ field }) => (
                <TextField {...field} required label="Other Reason" sx={{ gridColumn: "span 2" }} />
              )}
              control={control}
              name="reasonForLeavingOther"
            />
          )}
          <Controller
            render={({ field }) => (
              <TextField {...field} required label="Character" placeholder="e.g. Very Satisfactory" />
            )}
            control={control}
            name="character"
          />
          <div />
          {OPTIONAL_FIELDS.map(({ name, label }) => (
            <Box key={name} sx={{ gridColumn: "span 2" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enabledFields[name]}
                    onChange={() => handleToggle(name)}
                    size="small"
                  />
                }
                label={label}
              />
              {enabledFields[name] && (
                <Controller
                  render={({ field }) => (
                    <TextField {...field} label={label} multiline minRows={2} fullWidth sx={{ mt: 0.5 }} />
                  )}
                  control={control}
                  name={name}
                />
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          form="leave-form"
          variant="contained"
          color="error"
          disabled={leaveMutation.isLoading}
        >
          {leaveMutation.isLoading ? "Processing..." : "Confirm & Mark as Left"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditCertificateDialog = ({ open, onClose, studentId, certificate }) => {
  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {
      character: certificate?.character || "",
      reasonForLeaving: certificate?.reasonForLeaving || "",
      reasonForLeavingOther: certificate?.reasonForLeavingOther || "",
      academicAchievements: certificate?.academicAchievements || "",
      coCurricularActivities: certificate?.coCurricularActivities || "",
      specialPostHeld: certificate?.specialPostHeld || "",
      scholarshipsAwards: certificate?.scholarshipsAwards || "",
    },
  });

  const selectedReason = watch("reasonForLeaving");

  const [enabledFields, setEnabledFields] = useState({
    academicAchievements: !!certificate?.academicAchievements,
    coCurricularActivities: !!certificate?.coCurricularActivities,
    specialPostHeld: !!certificate?.specialPostHeld,
    scholarshipsAwards: !!certificate?.scholarshipsAwards,
  });

  const handleToggle = (fieldName) => {
    setEnabledFields((prev) => {
      const newState = { ...prev, [fieldName]: !prev[fieldName] };
      if (!newState[fieldName]) {
        setValue(fieldName, "");
      }
      return newState;
    });
  };

  const updateMutation = useMutation((data) =>
    axios.put(`/students/${studentId}/leaving-certificate`, data)
  );

  const onSubmit = (data) => {
    updateMutation.mutate(data, {
      onSuccess: () => {
        toast.success("Certificate updated");
        queryClient.invalidateQueries(["student", studentId]);
        onClose();
      },
      onError: () => {
        toast.error("Something went wrong");
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Certificate Details</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="edit-cert-form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, mt: 1 }}
        >
          <Controller
            render={({ field }) => (
              <TextField {...field} required label="Character" />
            )}
            control={control}
            name="character"
          />
          <Controller
            render={({ field }) => (
              <FormControl fullWidth required>
                <InputLabel id="edit-reason-label">Reason for Leaving</InputLabel>
                <Select {...field} labelId="edit-reason-label" label="Reason for Leaving">
                  {LEAVING_REASONS.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            control={control}
            name="reasonForLeaving"
          />
          {selectedReason === "OTHER" && (
            <Controller
              render={({ field }) => (
                <TextField {...field} required label="Other Reason" sx={{ gridColumn: "span 2" }} />
              )}
              control={control}
              name="reasonForLeavingOther"
            />
          )}
          {OPTIONAL_FIELDS.map(({ name, label }) => (
            <Box key={name} sx={{ gridColumn: "span 2" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enabledFields[name]}
                    onChange={() => handleToggle(name)}
                    size="small"
                  />
                }
                label={label}
              />
              {enabledFields[name] && (
                <Controller
                  render={({ field }) => (
                    <TextField {...field} label={label} multiline minRows={2} fullWidth sx={{ mt: 0.5 }} />
                  )}
                  control={control}
                  name={name}
                />
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          form="edit-cert-form"
          variant="contained"
          disabled={updateMutation.isLoading}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditStudent = () => {
  const { id } = useParams();
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [editCertDialogOpen, setEditCertDialogOpen] = useState(false);
  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: "@page { size: A4 portrait; margin: 15mm; } * { font-size: 100% !important; }",
  });

  const {
    data: student,
    isLoading: isStudentLoading,
    isError: isStudentError,
  } = useQuery(["student", id], () =>
    axios(`/students/${id}`).then((res) => res.data)
  );

  const {
    data: classes,
    isLoading: isClassesLoading,
    isError: isClassesError,
  } = useQuery("classes", () => axios.get("/classes").then((res) => res.data));

  const {
    data: parents,
    isLoading: isParentsLoading,
    isError: isParentsError,
  } = useQuery("parents", () => axios.get("/parents").then((res) => res.data));

  const editStudentMutation = useMutation(
    (data) => axios.put(`/students/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["student", id]);
        queryClient.invalidateQueries("students");
      },
    }
  );

  if (isStudentLoading || isClassesLoading || isParentsLoading) return <CircularProgress />;
  if (isStudentError || isClassesError || isParentsError) return <div>Error</div>;

  const cert = student.leavingCertificate;

  return (
    <>
      <EditStudentForm
        key={`${student.id}-${student.isActive}`}
        student={student}
        classes={classes}
        parents={parents}
        editStudentMutation={editStudentMutation}
      />

      {/* Leaving Certificate Section */}
      <Box mt={4}>
        {cert ? (
          <Box>
            <Typography variant="h6" gutterBottom>
              Leaving Certificate
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1, mb: 2 }}>
              <Typography>
                <strong>Certificate No:</strong> {cert.certificateNumber}
              </Typography>
              <Typography>
                <strong>Issued Date:</strong> {cert.issuedDate}
              </Typography>
              <Typography>
                <strong>Date of Leaving:</strong> {cert.leavingDate}
              </Typography>
              <Typography>
                <strong>Reason:</strong>{" "}
                {cert.reasonForLeaving === "OTHER"
                  ? cert.reasonForLeavingOther
                  : LEAVING_REASONS.find((r) => r.value === cert.reasonForLeaving)?.label}
              </Typography>
              <Typography>
                <strong>Character:</strong> {cert.character}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button variant="contained" onClick={handlePrint}>
                Print Leaving Certificate
              </Button>
              <Button variant="outlined" onClick={() => setEditCertDialogOpen(true)}>
                Edit Certificate Details
              </Button>
            </Box>

            <EditCertificateDialog
              open={editCertDialogOpen}
              onClose={() => setEditCertDialogOpen(false)}
              studentId={id}
              certificate={cert}
            />
          </Box>
        ) : student.isActive ? (
          <Button
            variant="contained"
            color="error"
            onClick={() => setLeaveDialogOpen(true)}
          >
            Mark as Left
          </Button>
        ) : (
          <Chip label="Inactive (no leaving certificate on file)" color="warning" />
        )}

        <MarkAsLeftDialog
          open={leaveDialogOpen}
          onClose={() => setLeaveDialogOpen(false)}
          studentId={id}
        />
      </Box>

      {/* Hidden printable certificate */}
      {cert && (
        <Box ref={printRef} className="print">
          <LeavingCertificate student={student} certificate={cert} />
        </Box>
      )}
    </>
  );
};

export default EditStudent;
